import { ApolloError } from '@apollo/client';
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
  Link,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { DataLake, Util } from '@finley-technologies/finley-types';

import {
  AthenaTableGQLType,
  useGetLatestHistoricalReceivableResponseTapeValuesLazyQueryGQLType,
  ReceivableResponseTapeAthenaPaginatedResponseGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { ColumnSelector } from './ColumnSelector';
import { FilterDrawer } from './FilterDrawer';
import { ReceivablesTablePaginationControl } from './Pagination';
import { QueryFiltersPanel } from './QueryFilterPills';
import {
  DateRange,
  generateInitialDateRange,
  ReceivablesTableColumnDef,
  useTransformAthenaColumnsToColumnConfig,
  initializeReceivableResponseTapeQueryFilterLocalStorage,
  deactivateAllReceivableResponseTapeQueryFiltersInLocalStorage,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { formatAthenaSqlString } from 'packages/common-frontend/components/QueryBuilder';
import {
  frozenHeaderCellStyling,
  frozenHeaderRowStyling,
  frozenRowCellStyling,
  frozenTableStyling,
} from 'packages/common-frontend/styles';
import { useAthenaSchemaContext } from 'packages/organization/hooks/AthenaSchemaContext';

const parseReceivablesData = (
  data: Util.Maybe<ReceivableResponseTapeAthenaPaginatedResponseGQLType>,
) => {
  if (!data)
    return {
      receivables: [],
      paginationMetadata: {
        NextToken: undefined,
        PreviousToken: undefined,
        paginationTokens: [],
      },
    };

  const { receivables: receivablesData, paginationMetadata } = data;

  const receivables = JSON.parse(receivablesData) ?? [];
  return { receivables, paginationMetadata };
};

const getInitialColumnVisibility = (
  columns: ReceivablesTableColumnDef[],
): VisibilityState => {
  const output: VisibilityState = {};
  columns.forEach((columnDef) => {
    output[columnDef.meta.key] = columnDef.meta.initiallyVisible;
  });
  return output;
};

export const ReceivablesTableBody: React.FC<{
  organizationId: string;
  creditFacilityId: string;
  columns: ReceivablesTableColumnDef[];
  receivables: any[];
  paginationMetadata: any;
  reportingDatesRange: DateRange;
  setDateRangeFilter: React.Dispatch<React.SetStateAction<DateRange>>;
  refetchReceivables: () => void;
  receivablesError?: ApolloError;
  isLoading: boolean;
  receivablesTableSchema: AthenaTableGQLType | undefined;
}> = ({
  organizationId,
  creditFacilityId,
  receivables,
  columns,
  paginationMetadata,
  reportingDatesRange,
  setDateRangeFilter,
  refetchReceivables,
  receivablesError,
  isLoading,
  receivablesTableSchema,
}) => {
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(
    columns.reduce((acc, columnDef) => {
      if (columnDef.id) {
        // eslint-disable-next-line no-param-reassign
        acc[columnDef.id] = columnDef.meta?.initialVisibility;
      }
      return acc;
    }, {} as Record<string, boolean>),
  );
  const table = useReactTable({
    data: receivables,
    columns,
    state: {
      columnVisibility,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
      columnVisibility: getInitialColumnVisibility(columns),
    },
    autoResetPageIndex: false,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mt="32px">
        <ColumnSelector tableInstance={table} />
        <FilterDrawer
          organizationId={organizationId}
          creditFacilityId={creditFacilityId}
          columnDefs={columns}
          initialDateRangeFilter={reportingDatesRange}
          onSetDateRange={setDateRangeFilter}
          receivablesTableSchema={receivablesTableSchema}
        />
      </Flex>
      <QueryFiltersPanel
        organizationId={organizationId}
        refetchReceivables={refetchReceivables}
      />

      <TableContainer
        mt="32px"
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
      >
        <Table style={frozenTableStyling}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding || '8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                    borderBottom="2px solid"
                    height="64px"
                    style={frozenHeaderCellStyling(
                      header.id === 'externalreceivableid',
                    )}
                  >
                    <Text>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Text>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          {(isLoading || receivablesError) && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {columns.map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                      style={{
                        ...frozenRowCellStyling(
                          column.id === 'externalreceivableid',
                        ),
                        ...{
                          minWidth: [
                            'externalreceivableid',
                            'finleyreceivableid',
                            'loanproductid',
                          ].includes(column.meta.key.toLowerCase())
                            ? '350px'
                            : 'auto',
                        },
                      }}
                    >
                      <Skeleton h="100%" />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isLoading && !receivablesError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.original.externalreceivableid}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={`${cell.id}+${row.original.externalreceivableid}`}
                      textAlign={cell.column.columnDef.meta?.style?.textAlign}
                      padding={
                        cell.column.columnDef.meta?.style?.padding || '8px 16px'
                      }
                      height="56px"
                      style={frozenRowCellStyling(
                        cell.column.id === 'externalreceivableid',
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && receivables.length === 0 && (
        <Alert mt="32px">
          <AlertIcon />
          <AlertDescription>
            No receivables match the selected filters.
          </AlertDescription>
        </Alert>
      )}
      <ReceivablesTablePaginationControl
        table={table}
        NextToken={paginationMetadata?.NextToken}
        PreviousToken={paginationMetadata?.PreviousToken}
        paginationTokens={paginationMetadata?.paginationTokens}
      />
    </Box>
  );
};

export const ReceivablesTable: React.FC<{
  isLenderPage?: boolean;
  organizationId: string;
  creditFacilityId: string;
  creditFacilitySlug: string;
  loanProductId: string;
}> = ({
  isLenderPage = true,
  organizationId,
  creditFacilitySlug,
  creditFacilityId,
  loanProductId,
}) => {
  const history = useHistory();
  const { getTableSchema } = useAthenaSchemaContext();

  const tableSchema = getTableSchema(
    `${DataLake.TablePrefix.ReceivableResponseTapes.toString()}_${creditFacilitySlug.toLowerCase()}`,
  );

  const { columns } = useTransformAthenaColumnsToColumnConfig({
    isLenderPage,
    columnProperties: tableSchema ? tableSchema.properties : [],
    creditFacilityId,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [reportingDatesRange, setDateRangeFilter] = useState<DateRange>(
    generateInitialDateRange(),
  );

  const [
    getReceivablesData,
    { data: receivablesData, error: receivablesError },
  ] = useGetLatestHistoricalReceivableResponseTapeValuesLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const refetchReceivables = useCallback(() => {
    // has to be defined within the callback otherwise infinitely re-renders
    const queryFilters = initializeReceivableResponseTapeQueryFilterLocalStorage(
      { organizationId, creditFacilityId },
    )[`${organizationId}+${creditFacilityId}`];
    const activeQueryFilters = queryFilters.filter(
      (queryFilter) =>
        queryFilter.isActive && queryFilter.query.rules.length > 0,
    );

    const activeQueryFilterStrs =
      activeQueryFilters.length > 0
        ? activeQueryFilters.map((queryFilter) =>
            formatAthenaSqlString(
              queryFilter.query,
              tableSchema?.properties || [],
            ),
          )
        : null;

    getReceivablesData({
      variables: {
        creditFacilityId,
        loanProductId: loanProductId === 'all' ? null : loanProductId,
        reportingDatesRange,
        queryFilters: activeQueryFilterStrs
          ? JSON.stringify(activeQueryFilterStrs)
          : null,
      },
    });
    setIsLoading(true);
  }, [
    creditFacilityId,
    getReceivablesData,
    loanProductId,
    organizationId,
    reportingDatesRange,
    tableSchema?.properties,
  ]);

  useEffect(() => {
    refetchReceivables();
  }, [refetchReceivables]);

  const queryFilters = initializeReceivableResponseTapeQueryFilterLocalStorage({
    organizationId,
    creditFacilityId,
  })[creditFacilityId];

  if (receivablesError) {
    // only deactivate filters + refresh page if there are any active filters
    if (queryFilters.length > 0) {
      deactivateAllReceivableResponseTapeQueryFiltersInLocalStorage({
        organizationId,
        creditFacilityId,
      });
      setTimeout(() => {
        history.go(0);
      }, 2000);
    }
  }

  const { receivables, paginationMetadata } = parseReceivablesData(
    receivablesData?.latestHistoricalReceivableResponseTapeValues,
    // mockResponse.data.latestHistoricalReceivableResponseTapeValues,
  );

  return (
    <>
      {isLoading && !receivablesError && (
        <>
          <Alert status="info" m="32px 0">
            <AlertIcon />
            <AlertDescription>
              Your receivables are loading and should be ready in just a few
              seconds.
            </AlertDescription>
          </Alert>
        </>
      )}
      {receivablesError && (
        <>
          <Alert status="error" m="32px 0">
            <AlertIcon />
            <AlertDescription>
              {queryFilters.length > 0 ? (
                <Text>
                  We&apos;ve encountered an issue with our filters. Temporarily,
                  filters are disabled while we refresh the data without them.
                </Text>
              ) : (
                <Text>
                  We&apos;ve encountered an issue.{' '}
                  <Link
                    href="mailto:support@finleycms.com"
                    color={colors.info}
                    textDecoration="underline"
                  >
                    Contact
                  </Link>{' '}
                  your Finley Customer Service Manager for assistance.
                </Text>
              )}
            </AlertDescription>
          </Alert>
        </>
      )}
      {!receivablesError && (
        <ReceivablesTableBody
          organizationId={organizationId}
          creditFacilityId={creditFacilityId}
          paginationMetadata={paginationMetadata}
          receivables={receivables}
          columns={columns}
          reportingDatesRange={reportingDatesRange}
          setDateRangeFilter={setDateRangeFilter}
          refetchReceivables={refetchReceivables}
          isLoading={isLoading}
          receivablesError={receivablesError}
          receivablesTableSchema={tableSchema}
        />
      )}
    </>
  );
};
